import React, { useEffect, useState } from "react";
import { Bell, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const TYPES = ["System", "Cashback", "Promo"];
const TARGETS = [["all_customers", "All customers"], ["all_merchants", "All merchants"], ["selected", "Selected (user IDs)"]];

export default function Notifications() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", type: "System", target: "all_customers", recipients: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/notifications").then((r) => setList(r.data.notifications)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and message required"); return; }
    setBusy(true);
    try {
      const recipients = form.target === "selected" ? form.recipients.split(",").map((s) => s.trim()).filter(Boolean) : [];
      const { data } = await api.post("/admin/notifications", { title: form.title, body: form.body, type: form.type, target: form.target, recipients });
      toast.success(`Sent to ${data.delivered} recipients`);
      setForm({ title: "", body: "", type: "System", target: "all_customers", recipients: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-notifications">
      <div className="flex items-center gap-2"><Bell className="h-5 w-5 text-amber-400" /><h1 className="font-heading text-2xl font-bold">Notifications</h1></div>

      <div className="ledger-card rounded-2xl p-6" data-testid="notification-compose">
        <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Compose broadcast</p>
        <div className="mt-4 space-y-3">
          <input data-testid="notification-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm outline-none focus:border-amber-500" />
          <textarea data-testid="notification-body" rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Message" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm outline-none focus:border-amber-500" />
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Type</label>
              <select data-testid="notification-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm">{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select>
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Target</label>
              <select data-testid="notification-target" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm">{TARGETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            </div>
          </div>
          {form.target === "selected" && (
            <input data-testid="notification-recipients" value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder="Comma-separated user IDs" className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm outline-none focus:border-amber-500" />
          )}
          <button data-testid="notification-send" onClick={send} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send</button>
        </div>
      </div>

      <div className="ledger-card overflow-hidden rounded-2xl">
        <p className="p-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">Sent history</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="notifications-table">
            <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-4 text-left">Date</th><th className="p-4 text-left">Title</th><th className="p-4 text-left">Type</th><th className="p-4 text-left">Target</th><th className="p-4 text-right">Delivered</th></tr></thead>
            <tbody>
              {list.map((n) => (
                <tr key={n.notification_id} className="border-t border-slate-800/60">
                  <td className="p-4 text-slate-400">{new Date(n.created_at).toLocaleString("en-IN")}</td>
                  <td className="p-4">{n.title}</td>
                  <td className="p-4 font-mono text-xs text-amber-300">{n.type}</td>
                  <td className="p-4 font-mono text-xs">{n.target}</td>
                  <td className="p-4 text-right font-mono">{n.delivered}</td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No notifications sent yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
